'use client'

import { Switch } from '@/app/components/common/Switch'
import { Icons } from '@/app/components/ui/Icons'
import { FILTER, TABLET_DOWN } from '@/app/constants'
import eventBus from '@/app/eventBus'
import { emitListViewChangeGAEvent } from '@/app/ga-event-helpers'
import { useMapView } from '@/app/hooks/useMapView'
import { PS_EVENTS } from '@/app/ps-events'
import { useMediaQuery } from '@uidotdev/usehooks'
import { useTranslations } from 'next-intl'
import dynamic from 'next/dynamic'
import { FC, useCallback, useState } from 'react'
import { DestPagesProps, FiltersCountMap, SORT_BY } from '../types'
import HotelListSorting from './HotelListSorting'

export interface Props {
	pageProps: DestPagesProps
	defaultStars?: number
	availableRates: { min: number; max: number }
	isFallbackData?: boolean
	isSingleHotels?: boolean
	isSimilarHotels?: boolean
	hasFilters?: boolean
	availableFilters: FiltersCountMap
}

const HotelListFilters = dynamic(() => import('./HotelListFilters'), {
	ssr: false,
})

const SortingAndFilters: FC<Props> = ({
	pageProps,
	availableRates,
	isFallbackData,
	isSingleHotels,
	hasFilters,
	availableFilters,
	isSimilarHotels,
}) => {
	const [openedFilter, setOpenedFilter] = useState<string | null>(null)
	const { isListView, setListView, isFullScreenMap } = useMapView();

	const tabletDown = useMediaQuery(TABLET_DOWN)

	const t = useTranslations('Keywords')

	const setFilterHash = useCallback((filter: string) => {
		if (typeof window !== 'undefined') {
			window.location.hash = filter
		}
	}, [])

	const handleListViewChange = useCallback((checked: boolean) => {
		emitListViewChangeGAEvent(checked)
		eventBus.publish(PS_EVENTS.LIST_VIEW, checked)
		setListView(checked)
	}, [])

	const removeFilterHash = useCallback(() => {
		if (typeof window !== 'undefined') {
			try {
				window.history.replaceState(
					null,
					document.title,
					window.location.pathname + window.location.search
				)
			} catch (error) {
				console.error('Error removing hash:', error)
			}
		}
	}, [])

	const handleOpenFilter = useCallback(
		(filterToOpen: string | null) => {
			setOpenedFilter(filterToOpen)
			if (filterToOpen) {
				setFilterHash(filterToOpen)
			} else {
				removeFilterHash()
			}
		},
		[setFilterHash, removeFilterHash]
	)

	const handleHashChange = useCallback((hash: string | null) => {
		if (hash) {
			window.history.replaceState(null, '', hash)
		} else {
			window.history.replaceState(
				null,
				'',
				window.location.pathname + window.location.search
			)
		}
	}, [])

	return (
		<div className="flex max-md:mb-2 mt-2 md:mt-0 gap-3 md:justify-between md:w-full xl:w-auto max-md:w-auto sorting-filters">
			{!isSingleHotels && ((tabletDown && isFullScreenMap) || !isFallbackData) && (
				<HotelListFilters
					pageProps={pageProps}
					onOpen={() => handleOpenFilter(FILTER.FILTERS)}
					onClose={() => handleOpenFilter(null)}
					onHashChange={handleHashChange}
					availableRates={availableRates}
					availableFilters={availableFilters}
				/>
			)}
			{!hasFilters && (
				<HotelListSorting
					pageProps={pageProps}
					defaultValueBy={SORT_BY.POPULARITY}
					onOpen={() => handleOpenFilter(FILTER.SORTING)}
					onClose={() => handleOpenFilter(null)}
					onHashChange={handleHashChange}
				/>
			)}
			{!tabletDown && (
				<Switch
					aria-label='Toggle List/Map View'
					onText={t('list')}
					offText={t('map')}
					offIcon={<Icons.map />}
					onIcon={<Icons.list />}
					onChange={handleListViewChange}
					checked={isListView}
				/>
			)}
		</div>
	)
}

export default dynamic(() => Promise.resolve(SortingAndFilters), { ssr: false })
